import {
    Avatar,
    Box,
    Divider,
    Grid,
    Link,
    Stack,
    Typography,
    useMediaQuery,
} from "@mui/material";
import React from "react";
import { Redirect, useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import toRupiah from "../../utils/toRupiah";
import ThemeDefaultComponent from "../../components/ThemeDefaultComponent/ThemeDefaultComponent";
import Spinner from "../../components/Spinner/Spinner";
import useCekStruk from "../../services/queries/useCekStruk";
import LogoUloWhite from "../../components/LogoUlo/LogoUloWhite";
import moment from "moment";
import "moment/locale/id";
import { MAIN_COLOR } from "../../constants/colors";
import { getLocalUkmId } from "../../utils/handleChangeUkmId";
import useUser from "../../services/queries/useUser";
import useUkm from "../../services/queries/useUkm";
// import hexToRGBA from "../../utils/hexToRGBA";


interface IHalamanStrukProps {}

type RouteParams = {
    slug: string;
};

const HalamanStruk = (props: IHalamanStrukProps) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
    let { slug } = useParams<RouteParams>();

    const { data: user } = useUser();

    const initialParamsUkm = {
        size: 500,
        page: 1,
        search: "",
    };
    const { data: ukm } = useUkm(initialParamsUkm);
    const ukmId = React.useMemo(() => {
        if (user?.userId) {
            return getLocalUkmId(user?.userId);
        }
        return null;
    }, [user?.userId]);

    const ukmData = React.useMemo(() => {
        if (ukmId) {
            return ukm?.content.find((li) => li.id === ukmId);
        }
        return ukm?.content[0];
    }, [ukm?.content, ukmId]);    

    const { data: cekStruk, status: statusCekStruk } = useCekStruk(slug);
    
    const lightenColor = (hex: string, amount: number) => {
        const num = parseInt(hex.replace("#", ""), 16);

        const r = Math.min(255, Math.round(((num >> 16) & 255) + (255 - ((num >> 16) & 255)) * amount));
        const g = Math.min(255, Math.round(((num >> 8) & 255) + (255 - ((num >> 8) & 255)) * amount));
        const b = Math.min(255, Math.round((num & 255) + (255 - (num & 255)) * amount));

        return `rgb(${r}, ${g}, ${b})`;
    };

    if (statusCekStruk === "loading") {
        return (
            <ThemeDefaultComponent>
                <Box
                    sx={{
                        backgroundColor: "#b5dcc9",
                        position: "fixed",
                        width: "100vw",
                        height: "100vh",
                        top: "0px",
                        left: "0px",
                        zIndex: -10,
                    }}
                />
                <Spinner />
            </ThemeDefaultComponent>
        );
    }

    if (statusCekStruk === "error") {
        return (
            <Redirect
                to={{
                    pathname: "/404",
                }}
            />
        );
    }

    const isFormattedDate = (dateStr: moment.MomentInput) => {
        return moment(dateStr, "DD MMM YYYY HH:mm", true).isValid();
    };

    // const formatDate = (date: string) => {
    //     return moment(date).format("DD MMM YYYY, HH:mm");
    // };

    const hasLogo = !!cekStruk?.gambarToko;

    return (
        <ThemeDefaultComponent>
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    padding: fullScreen ? "8px" : "4%",
                    paddingY: "4%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    backgroundColor: ukmData?.warna
                        ? lightenColor(ukmData.warna, 0.2)
                        : MAIN_COLOR,
                    minHeight: "100vh",
                }}
            >
                <Box
                    sx={{
                        width: "380px",
                        maxWidth: "100%",
                        backgroundColor: "#ffffff",
                        borderRadius: "12px",
                        overflow: "hidden",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                        padding: "24px 20px",
                        marginBottom: "16px",
                    }}
                >
                    <Box 
                        display="flex" 
                        flexDirection="row" 
                        alignItems="center"
                        padding={1} 
                        mb={2} 
                        gap={1}
                        sx={{
                            backgroundColor: "#F5F5F5",
                            borderRadius: "8px",
                        }}
                    >
                        {hasLogo && (
                            <Box mb={1.5}>
                                <Avatar
                                    src={cekStruk?.gambarToko ?? undefined}
                                    sx={{
                                        width: 70,
                                        height: 70,
                                        border: "3px solid #e8edf2",
                                    }}
                                />
                            </Box>
                        )}

                        <Box
                            sx={{
                                textAlign: hasLogo ? "left" : "center",
                                width: "100%",
                            }}
                        >
                            <Typography
                                variant="h6"
                                textAlign="center"
                                fontWeight={700}
                                sx={{
                                    fontSize: hasLogo ? "18px" : "20px",
                                    letterSpacing: "0.5px",
                                    color: "#1a1a2e",
                                }}
                            >
                                {cekStruk?.namaUkm || "Store Name"}
                            </Typography>

                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="center"
                                spacing={0.5}
                                mt={0.5}
                            >
                                <Typography textAlign="center" variant="caption" color="#666">
                                    +{cekStruk?.noTelp} | {cekStruk?.alamatUkm}
                                </Typography>
                            </Stack>
                        </Box>
                    </Box>

                    <Box
                        padding={1}
                        sx={{
                            backgroundColor: "#F5F5F5",
                            borderRadius: "8px",
                        }}
                    >
                        <Grid 
                            container 
                            spacing={1} 
                            sx={{ 
                                mb: 1.5,
                            }}
                        >
                            <Grid item xs={6}>
                                <Typography variant="caption" color="#888" fontWeight={500}>
                                    Tanggal
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                    {isFormattedDate(cekStruk?.tanggalTransaksi)
                                        ? cekStruk?.tanggalTransaksi
                                        : moment(cekStruk?.tanggalTransaksi).format("DD MMM YYYY HH:mm")}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="#888" fontWeight={500}>
                                    NO Invoice
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                    {cekStruk?.nomorInvoice}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="#888" fontWeight={500}>
                                    Metode
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                    {cekStruk?.metode || "Tunai"}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="#888" fontWeight={500}>
                                    Kasir
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                    {cekStruk?.kasir || "-"}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box mt={2}>
                        {cekStruk?.daftarItem.map((items, index) => (
                            <Box key={index} sx={{ mb: 1.5 }}>
                                <Grid container alignItems="center">
                                    <Grid item xs={4}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {items.namaItem}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={5} textAlign="center">
                                        <Typography variant="body2" fontWeight={500}>
                                            {toRupiah(items.hargaSatuan)} x {items.jumlahItem}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={3} textAlign="right">
                                        <Typography variant="body2" fontWeight={600}>
                                            {toRupiah(items.hargaSatuan * items.jumlahItem)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        ))}
                    </Box>

                    <Divider 
                        sx={{ 
                            borderColor: "#e0e0e0", 
                            borderStyle: "dashed",
                            borderWidth: "1px",
                            my: 1.5 
                        }} 
                    />

                    <Box sx={{ mt: 1 }}>
                        <Stack direction="row" justifyContent="space-between" py={0.5}>
                            <Typography variant="body2" color="#888">Subtotal</Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {toRupiah(Number(cekStruk?.totalHarga))}
                            </Typography>
                        </Stack>

                        {/* Diskon */}
                        <Stack direction="row" justifyContent="space-between" py={0.5}>
                            <Typography variant="body2" color="#888">Diskon</Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {toRupiah(Number(cekStruk?.diskon))}
                            </Typography>
                        </Stack>

                        {/* Pajak */}
                        <Stack direction="row" justifyContent="space-between" py={0.5}>
                            <Typography variant="body2" color="#888">
                                {cekStruk?.pajakPersen
                                    ? `Pajak (${cekStruk?.pajakPersen}%)`
                                    : "Pajak"}
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {toRupiah(Number(cekStruk?.pajak))}
                            </Typography>
                        </Stack>

                        {/* Service */}
                        <Stack direction="row" justifyContent="space-between" py={0.5}>
                            <Typography variant="body2" color="#888">
                                {cekStruk?.serviceCustomPersen
                                    ? `Service (${cekStruk?.serviceCustomPersen}%)`
                                    : "Service"}
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {toRupiah(Number(cekStruk?.serviceCustom))}
                            </Typography>
                        </Stack>


                        <Box
                            mt={1}
                            padding={1}
                            sx={{
                                border: "1px solid #DFDFDF",
                                borderRadius: "8px",
                            }}
                        >
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Typography variant="body1" fontWeight={700}>Total</Typography>
                                <Typography variant="h6" fontWeight={700} color="#1a1a2e">
                                    {toRupiah(Number(cekStruk?.totalPembayaran))}
                                </Typography>
                            </Stack>
                        </Box>


                        <Stack direction="row" justifyContent="space-between" py={0.5} mt={1}>
                            <Typography variant="body2" color="#888">Uang Dibayar</Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {toRupiah(Number(cekStruk?.uangYangDibayar))}
                            </Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" py={0.5}>
                            <Typography variant="body2" color="#888">Kembalian</Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {toRupiah(Number(cekStruk?.kembalian))}
                            </Typography>
                        </Stack>
                    </Box>


                    <Box textAlign="center">
                        <Typography variant="body2" fontWeight={500} color="#1a1a2e">
                            {cekStruk?.ukm.customStruk.text}
                        </Typography>
                    </Box>
                </Box>

                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    py={1}
                >
                    <Typography variant="caption" color="#888" fontWeight={500}>
                        Powered by
                    </Typography>
                    <Link
                        href="https://ukmlokal.com"
                        target="_blank"
                        rel="noreferrer"
                        underline="none"
                        display="flex"
                        alignItems="center"
                    >
                        <LogoUloWhite height={40} width={40} />
                    </Link>
                </Box>
            </Box>
        </ThemeDefaultComponent>
    );
};

export default HalamanStruk;